import { useEffect, useState } from 'react';
import SwipeableViews from 'react-swipeable-views';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import {
  InventorySKUListing,
  MetaData,
} from '@views/StockBySKU/components/types';
import { Badge } from '@tailwindComponents/Badge';
import { numberToLocale } from '@forms/client/AddProductForm/utils';
import { INVENTORY_IMAGES_URL } from '@constants';
import { useAuth } from '@context';
import { UserRoleService } from '@services';
import {
  formatPalletDimensions,
  formatPalletWeight,
  formatSkuPrice,
  palletStackedCube,
  palletStackedWeight,
  skuBarcode,
  skuCode,
  skuDescription,
  skuInformationItems,
  skusPerPalletWithUnit,
  stockWithProducts,
} from '@views/SKUMaster/components/skuHelpers';
import { DottedTextList } from '../DottedTextList';
import { Ean13Barcode } from '../Ean13Barcode';
import { PackagingHierarchy } from '../PackagingHierarchy';
import { PanelFieldBox, notSet } from '../PanelFieldBox';
import { lostStockOf, surplusStockOf } from '../../utils/stockDisplay';
import { skuPackLabel, skuUnitNoun } from '../../utils/sku';

type Price = InventorySKUListing['costPrices'][number];

function SkuImages({ sku }: { sku: InventorySKUListing }) {
  const [activeImageStep, setActiveImageStep] = useState(0);

  useEffect(() => {
    setActiveImageStep(0);
  }, [sku.inventorySkuId]);

  const images = [
    ...(sku.images || []),
    ...(sku.productImages || []),
    ...(sku.productDetails?.image ? [sku.productDetails.image] : []),
  ];

  const sourceOf = (image: { imageLocation: string; remoteName: string }) =>
    `${INVENTORY_IMAGES_URL}/${image.imageLocation}/big/${image.remoteName}`;

  if (images.length === 0) {
    return (
      <div className='flex h-[200px] items-center justify-center rounded-lg bg-gray-100'>
        <span className='text-sm text-gray-400'>No images available</span>
      </div>
    );
  }

  if (images.length === 1) {
    return (
      <div className='flex w-full justify-center'>
        <img
          src={sourceOf(images[0])}
          alt={images[0].title || sku.name}
          className='block h-[300px] w-full rounded-lg object-contain'
        />
      </div>
    );
  }

  return (
    <div>
      <div className='overflow-hidden'>
        <SwipeableViews
          enableMouseEvents
          index={activeImageStep}
          onChangeIndex={(step: number) => setActiveImageStep(step)}
        >
          {images.map((image, index) => (
            <img
              key={image.id}
              src={sourceOf(image)}
              alt={image.title || `Image ${index + 1}`}
              className={`block h-[300px] w-full rounded-lg object-contain ${
                index === activeImageStep ? '' : 'hidden'
              }`}
            />
          ))}
        </SwipeableViews>
      </div>
      <div className='mt-2 flex items-center justify-between rounded bg-gray-100 px-2 py-1'>
        <button
          type='button'
          aria-label='Previous image'
          className='px-2 py-1 text-gray-700 disabled:opacity-50'
          onClick={() => setActiveImageStep((step) => step - 1)}
          disabled={activeImageStep === 0}
        >
          <ChevronLeftIcon className='h-5 w-5' />
        </button>
        <span className='text-sm text-gray-500'>
          {activeImageStep + 1} / {images.length}
        </span>
        <button
          type='button'
          aria-label='Next image'
          className='px-2 py-1 text-gray-700 disabled:opacity-50'
          onClick={() => setActiveImageStep((step) => step + 1)}
          disabled={activeImageStep === images.length - 1}
        >
          <ChevronRightIcon className='h-5 w-5' />
        </button>
      </div>
    </div>
  );
}

function CollapsibleSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className='rounded-lg border border-gray-200'>
      <button
        type='button'
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        className='flex w-full items-center justify-between gap-4 px-3 py-2 text-left'
      >
        <span className='font-semibold text-gray-900'>{title}</span>
        <ChevronDownIcon
          className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className='border-t border-gray-100 px-3 py-3'>{children}</div>
      )}
    </div>
  );
}

function MetaDataList({ title, items }: { title: string; items: MetaData[] }) {
  return (
    <div>
      <div className='mb-1 font-semibold'>{title}</div>
      <ul className='ml-4 list-disc'>
        {items.map((item) => (
          <li key={item.name + item.value}>
            <span className='font-medium'>{item.name}:</span> {item.value}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PriceList({ title, prices }: { title: string; prices: Price[] }) {
  return (
    <div>
      <div className='mb-2 font-semibold'>{title}</div>
      <ul className='flex flex-col gap-1 text-sm text-gray-900'>
        {prices.map((price, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={index}>
            {formatSkuPrice(price)}
            {price.comment && (
              <span className='ml-2 text-gray-500'>{price.comment}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SkuInfoContent({ sku }: { sku: InventorySKUListing }) {
  const { user } = useAuth();
  const { isWarehouseClient } = UserRoleService();
  const canSeePrices = isWarehouseClient(user);

  const unitNoun = skuUnitNoun(sku);
  const description = skuDescription(sku);
  const barcode = skuBarcode(sku);
  const packLabel = skuPackLabel(sku, sku.name);
  const costPrices = canSeePrices ? sku.costPrices ?? [] : [];
  const listPrices = canSeePrices ? sku.listPrices ?? [] : [];
  const skuMetaData = sku.metaData ?? [];
  const productMetaData = sku.productMetaData ?? [];

  return (
    <div className='flex flex-col gap-6 p-4'>
      <div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
        <h2 className='text-lg font-semibold text-gray-900'>{sku.name}</h2>
        {packLabel && (
          <span className='text-sm text-gray-500'>{packLabel}</span>
        )}
        <Badge
          intent={sku.isArchived ? 'neutral' : 'info'}
          label={sku.isArchived ? 'Archived' : 'Active'}
        />
      </div>

      <div className='flex flex-col gap-4 md:flex-row'>
        <div className='md:w-[260px] md:shrink-0'>
          <SkuImages sku={sku} />
        </div>
        <div className='flex min-w-0 flex-1 flex-col gap-2.5'>
          <PanelFieldBox
            isEmphasised
            label='SKU ID'
            isEditing={false}
            editor={null}
            value={skuCode(sku)}
          />
          <PanelFieldBox
            isEmphasised
            label='SKU Name'
            isEditing={false}
            editor={null}
            value={sku.name || notSet}
          />
          <PanelFieldBox
            isEmphasised
            label='Barcode'
            isEditing={false}
            editor={null}
            value={
              barcode ? (
                <>
                  <span className='tracking-wider'>{barcode}</span>
                  <Ean13Barcode barcode={barcode} className='mt-1 block' />
                </>
              ) : (
                notSet
              )
            }
          />
          <PanelFieldBox
            isOptional
            label='Description'
            isEditing={false}
            editor={null}
            value={
              description ? (
                <p className='whitespace-pre-line'>{description}</p>
              ) : (
                notSet
              )
            }
          />
        </div>
      </div>

      <PackagingHierarchy sku={sku} />
      <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
        <div className='flex flex-col gap-4'>
          <div>
            <div className='mb-2 text-lg font-semibold'>SKU Information</div>
            <DottedTextList items={skuInformationItems(sku)} />
          </div>
          {skuMetaData.length > 0 && (
            <MetaDataList title='SKU Metadata' items={skuMetaData} />
          )}
          {productMetaData.length > 0 && (
            <MetaDataList title='Product Metadata' items={productMetaData} />
          )}
        </div>
        <div>
          <div className='mb-2 font-semibold'>Stock By Warehouse</div>
          {sku.stockByWarehouse && sku.stockByWarehouse.length > 0 ? (
            <div className='flex flex-col gap-4'>
              {sku.stockByWarehouse.map((stock) => (
                <div key={stock.id} className='rounded border p-2'>
                  <div className='mb-1 font-medium'>{stock.warehouseName}</div>
                  <DottedTextList
                    items={[
                      {
                        label: 'Available Stock',
                        value: stockWithProducts(sku, stock.availableStock),
                      },
                      {
                        label: 'Allocated Stock',
                        value: stockWithProducts(sku, stock.allocatedStock),
                      },
                      {
                        label: 'Blocked Stock',
                        value: stockWithProducts(sku, stock.blockedStock),
                      },
                      {
                        label: 'In Transit Stock',
                        value: stockWithProducts(
                          sku,
                          stock.inInboundTransitStock
                        ),
                      },
                      {
                        label: 'Total Stock',
                        value: stockWithProducts(sku, stock.totalStock),
                      },
                      {
                        label: 'Lost Stock',
                        value: numberToLocale(lostStockOf(stock)),
                      },
                      {
                        label: 'Surplus Stock',
                        value: numberToLocale(surplusStockOf(stock)),
                      },
                    ]}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className='text-gray-500'>No warehouse stock data.</div>
          )}
        </div>
      </div>
      {sku.palletising && sku.palletising.length > 0 && (
        <div>
          <div className='mb-2 font-semibold'>
            {sku.palletising.length > 1 ? 'Palletising options' : 'Palletising'}
          </div>
          <div className='flex flex-col gap-2'>
            {sku.palletising.map((pallet) => (
              <div key={pallet.id} className='rounded border p-2'>
                <div className='font-medium'>{pallet.type}</div>
                <DottedTextList
                  items={[
                    {
                      label: `${unitNoun}s per layer`,
                      value: pallet.amountOfSkusInLayer?.toString() ?? '—',
                    },
                    {
                      label: '# of layers',
                      value: pallet.amountOfLayersInPallet?.toString() ?? '—',
                    },
                    {
                      label: 'Dimensions',
                      value: formatPalletDimensions(pallet),
                    },
                    {
                      label: 'Weight',
                      value: formatPalletWeight(pallet),
                    },
                    {
                      label: 'Cube',
                      value: palletStackedCube(sku, pallet),
                    },
                    {
                      label: 'Loaded weight',
                      value: palletStackedWeight(sku, pallet),
                    },
                  ]}
                />
                <p className='mt-1 text-sm text-gray-500'>
                  1 pallet = {skusPerPalletWithUnit(sku, pallet)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      {(costPrices.length > 0 || listPrices.length > 0) && (
        <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
          {listPrices.length > 0 && (
            <PriceList title='List Prices' prices={listPrices} />
          )}
          {costPrices.length > 0 && (
            <PriceList title='Cost Prices' prices={costPrices} />
          )}
        </div>
      )}
      {(sku.productDetails ||
        sku.productCategory ||
        sku.productSubCategory ||
        sku.brand ||
        sku.productName) && (
        <CollapsibleSection title='Product Information (optional)'>
          <DottedTextList
            items={[
              {
                label: 'Product ID',
                value: sku.productDetails?.productId ?? '-',
              },
              {
                label: 'Product Name',
                value: sku.productDetails?.name ?? sku.productName ?? '-',
              },
              {
                label: 'Brand',
                value: sku.brand ?? sku.productDetails?.brand ?? '-',
              },
              {
                label: 'Category',
                value:
                  sku.productCategory ?? sku.productDetails?.category ?? '-',
              },
              {
                label: 'Subcategory',
                value:
                  sku.productSubCategory ??
                  sku.productDetails?.subCategory ??
                  '-',
              },
              {
                label: 'Net Volume',
                value:
                  sku.productDetails?.netVolume != null
                    ? `${numberToLocale(sku.productDetails.netVolume)}${
                        sku.productDetails.netVolumeUnitOfMeasure ?? ''
                      }`
                    : '-',
              },
              {
                label: `Products per ${unitNoun}`,
                value:
                  (
                    sku.amountOfProductInSku ??
                    sku.productDetails?.amountOfProductInSku
                  )?.toString() ?? '-',
              },
              {
                label: 'Barcode',
                value: sku.productDetails?.barcode?.barcode ?? '-',
              },
            ]}
          />
        </CollapsibleSection>
      )}
    </div>
  );
}
